import React, { FC, useEffect, useRef, useState } from 'react'
import { ResourceListingContainer, ResourcePageOuter } from './style'

//* utils
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../../constants/localization.constants'
import { UseCases } from '../../../../constants/useCases.constants'
import { ComponentAnnouncement } from '../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../models/others/menuGroup'
import { kebabCaseString } from '../../../../utils/functions.utils'
import UrlUtil from '../../../../utils/url.utils'
import { ResourceListingBodySection } from './subcomponents/listingSection'
import { getMicrocopyData, getPageNumber } from './utils/get'
import { filteredResources } from './utils/handle'
import { addSearchQuery, updateURL } from './utils/url'

//* components
import ContentBlock from '../../../global/contentBlock'
import Hero from '../../../global/hero'
import ResourceRow from '../../../global/resourceRow'
import SEOTags from '../../../global/seoTags'
import Layout from '../../../layouts/layout'

type Props = {
  pageContext: any
  location?: any
  path: string
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData?: any
}

const ResourceCenterLandingPage: FC<Props> = ({
  pageContext,
  location,
  path,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  if (!pageContext.listingPageData) return null
  const listingPageData = {
    useCase: pageContext.listingPageData.useCase || '',
    microcopy: pageContext.listingPageData?.microcopy || {},
    seoMetadata: pageContext.listingPageData.seoMetadata || null,
    slug: pageContext.listingPageData.slug || '',
    flexSection: pageContext.listingPageData.flexSection || [],
    pageName: pageContext.listingPageData.pageName || '',
    metaInformation: pageContext.listingPageData.metaInformation || null,
    breadcrumb: pageContext.listingPageData.breadcrumb || null,
    filters: pageContext.listingPageData.filters || [],
  }

  const eventsData: any = {
    isEvents: listingPageData.useCase === UseCases.RESOURCE_LISTING_EVENTS,
    contentBlock: null,
    resourceRow: null,
    contentBlockHeadline: null,
  }

  const resourceData = pageContext.resourceData || []
  const featuredResources = pageContext.featuredResources || []
  const filterDrawers = useRef<any>({})

  const defaultLocale = pageContext.pageLocale || LocaleCodes.ENGLISH.code
  let defaultSlug = ''

  if (listingPageData.slug.charAt(0) === '/') {
    defaultSlug = listingPageData.slug.trim()
  } else {
    defaultSlug = `/${listingPageData.slug.trim()}`
  }

  const pageNumber = pageContext.pageNumber || 0

  const [queryStringFilterSet, setQueryStringFilterSet] = useState<any>(null)
  const [queryString, setQueryString] = useState<any>(null)
  const [checkboxFilters, setCheckboxFilters] = useState<any>([])
  const [filtersAdded, setFiltersAdded] = useState<any>([])
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false)
  const [listingData, setListingData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const resourcesPerPage = 12

  //* Only used internally not by the paging system
  const handlePaginationClick = (page: number, isFilter?: boolean) => {
    let urlQuery = typeof window !== 'undefined' ? window.location.search : ''

    if (page > 1) {
      history.pushState(null, '', `${defaultSlug}/${page}${urlQuery}`)
    } else {
      history.pushState(null, '', `${defaultSlug}${urlQuery}`)
    }

    //* If mobile drawer is open, this sends you to a weird position
    if (isFilter && !mobileDrawerOpen) {
      document
        ?.getElementById('ResourceListingTopFilterContainer')
        ?.scrollIntoView({ behavior: 'smooth' })
    } else if (typeof window !== undefined) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const clearSearch = () => {
    setSearchText('')
    setQueryString(queryStringFilterSet)

    if (searchText) {
      handlePaginationClick(1, true)
      history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'search'))
    }
  }

  const hrefForPage = (page: number) => {
    let url = ''
    const hasFilters = queryString && queryString !== null && queryString !== ''

    if (page === 1) {
      if (typeof window !== 'undefined') {
        if (hasFilters) {
          url = `${defaultSlug}${queryString}`
        } else {
          url = `${defaultSlug}`
        }
      } else if (defaultSlug) {
        url = `${defaultSlug}`
      }
    } else {
      if (typeof window !== 'undefined') {
        if (hasFilters) {
          url = `${defaultSlug}/${page}${queryString}`
        } else {
          url = `${defaultSlug}/${page}`
        }
      } else if (defaultSlug) {
        url = `${defaultSlug}/${page}`
      }
    }

    return url
  }

  const currentPage = getPageNumber(pageNumber)
  const start = (currentPage - 1) * resourcesPerPage
  const end = currentPage * resourcesPerPage

  const dynamicPageContent = listingPageData?.flexSection?.map((module: any, i: Number) => {
    switch (module.__typename) {
      case ModuleNames.HERO:
        return (
          <Hero
            data={module}
            location={location}
            pageTitle={listingPageData.pageName}
            showBreadcrumbs={true}
            key={`page-component--${i}`}
            pageType={'resourceListing'}
            metaInformation={listingPageData.metaInformation}
            pageUseCase={listingPageData.useCase}
            locale={defaultLocale}
            breadcrumb={listingPageData.breadcrumb}
          />
        )

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        if (listingPageData.useCase === 'Events') {
          module.contentBlockTheme = 'Dark'
          module.cta1Style = 'primary'
          eventsData.contentBlockHeadline = module.headline
        } else {
          module.cta1Style = 'Link'
          module.contentBlockTheme = 'Light'
        }

        eventsData.contentBlock = <ContentBlock data={module} key={`page-component--${i}`} />
        return ''

      case ModuleNames.RESOURCE_ROW:
        if (eventsData.isEvents) {
          eventsData.resourceRow = (
            <ResourceRow data={module} key={`page-component--${i}`} locale={defaultLocale} />
          )
        }

        return ''
    }
  })

  const handleFilterItemClick = (e: any, multiFilterArr?: any, newQueryString?: string) => {
    let filterGroupKebab = ''
    let filterValue = ''
    let newFilterArr = [...checkboxFilters]
    let newFiltersAddedArr = [...filtersAdded]

    //* set multiple filters at once
    if (multiFilterArr && newQueryString) {
      for (let x = 0; x < multiFilterArr.length; x++) {
        const filterIndex = multiFilterArr[x].checkboxIndex
        const updatedFilter = multiFilterArr[x].checkboxData
        const filterGroupName = multiFilterArr[x].checkboxData.filterGroup

        newFilterArr[filterIndex] = updatedFilter
        for (let i = 0; i < newFiltersAddedArr?.length; i++) {
          const filterAddedGroup = newFiltersAddedArr[i]

          //* If there is a filter group active, add to that existing group's selected filters array
          if (filterAddedGroup?.name === filterGroupName) {
            filterAddedGroup.filters.push(updatedFilter)
          }
        }
      }

      setCheckboxFilters(newFilterArr)
      setFiltersAdded(newFiltersAddedArr)
      setQueryString(addSearchQuery(newQueryString, searchText))
    } else {
      if (e.target.getAttribute('data-item')) {
        const filterGroupName = e.target.getAttribute('data-item')
        filterGroupKebab = kebabCaseString(filterGroupName)

        if (e.target.value) {
          filterValue = e.target.value

          if (e.target.getAttribute('data-idx')) {
            const filterIndex = e.target.getAttribute('data-idx')

            let newQueryString =
              queryStringFilterSet && queryStringFilterSet !== '?filterResources='
                ? queryStringFilterSet
                : '?filterResources='

            let newQueryStringItem = `&${filterGroupKebab}=${filterValue}`
            let updatedFilter = { ...newFilterArr[filterIndex] }
            const fieldUnchecked = !updatedFilter.checked === false

            if (fieldUnchecked) {
              newQueryString = newQueryString.replace(newQueryStringItem, '')
              if (newQueryString === '?filterResources=') {
                newQueryString = ''
              }
            } else {
              newQueryString += newQueryStringItem
            }

            updatedFilter.checked = !updatedFilter.checked

            for (let i = 0; i < newFiltersAddedArr?.length; i++) {
              const filterAddedGroup = newFiltersAddedArr[i]

              //* If there is a filter group active, add to that existing group's selected filters array
              if (filterAddedGroup?.name === filterGroupName) {
                if (filterAddedGroup.filters?.length === 0) {
                  filterAddedGroup.filters.push(updatedFilter)
                } else {
                  if (fieldUnchecked) {
                    for (let k = 0; k < filterAddedGroup.filters?.length; k++) {
                      //* if item already exists it should be removed
                      if (filterAddedGroup.filters[k].key === updatedFilter.key) {
                        filterAddedGroup.filters.splice(k, 1)
                      }
                    }
                  } else {
                    filterAddedGroup.filters.push(updatedFilter)
                  }
                }
              }
            }

            newFilterArr[filterIndex] = updatedFilter
            setCheckboxFilters(newFilterArr)
            setFiltersAdded(newFiltersAddedArr)
            setQueryStringFilterSet(newQueryString)
            setQueryString(addSearchQuery(newQueryString, searchText))
            handlePaginationClick(1, true)
          }
        }
      }
    }
  }

  const resetFilterState = (isInitialization?: boolean) => {
    let filterState: any = []
    let filterGroupState: any = []

    if (listingPageData.filters?.length > 0) {
      for (let i = 0; i < listingPageData.filters.length; i++) {
        const filterGroup = listingPageData.filters[i]

        filterGroupState.push({
          name: filterGroup.name || '',
          filters: [],
        })

        for (let k = 0; k < filterGroup.items?.length; k++) {
          const item = filterGroup.items[k]
          const filterItemName = item.title || item.customerName
          const strippedValue = kebabCaseString(filterItemName)

          filterState.push({
            key: `${strippedValue}`,
            checked: false,
            value: filterItemName,
            typename: item.__typename,
            filterGroup: filterGroup.name || '',
          })
        }
      }

      setCheckboxFilters(filterState)
      setFiltersAdded(filterGroupState)

      if (!isInitialization) {
        setQueryStringFilterSet('')
        clearSearch()
        setQueryString('')
      }
    }
  }

  const init = () => {
    if (checkboxFilters && checkboxFilters.length === 0) {
      resetFilterState(true)
    }

    if (!listingData || listingData?.length == 0) {
      setListingData(resourceData)
    }

    //* If there is a query string, we need to trigger the filters, only need to do this on initial render
    if (typeof window !== `undefined` && checkboxFilters?.length > 0 && queryString === null) {
      let checkboxesToSet = []

      if (window.location.href.includes('?filterResources=')) {
        const existingQueryString = window.location.href.split('?filterResources')
        let urlFiltersToApply = window.location.href.split('&')
        urlFiltersToApply.shift()

        if (urlFiltersToApply && urlFiltersToApply.length !== 0) {
          for (let i = 0; i < checkboxFilters?.length; i++) {
            const checkbox = { ...checkboxFilters[i] }

            for (let k = 0; k < urlFiltersToApply.length; k++) {
              const filter = urlFiltersToApply[k]
              const filterElements = filter.split('=')
              const filterGroupName = filterElements[0]
              const filterKey = filterElements[1]

              if (kebabCaseString(checkbox.filterGroup) === filterGroupName) {
                if (checkbox.key === filterKey) {
                  checkbox.checked = true

                  const checkBoxFilterInfo = {
                    checkboxIndex: i,
                    checkboxData: checkbox,
                  }

                  checkboxesToSet.push(checkBoxFilterInfo)
                }
              }
            }
          }

          let newQueryString =
            existingQueryString && existingQueryString[1]
              ? `?filterResources${existingQueryString[1]}`
              : ''

          //* remove search param if it exists
          newQueryString = newQueryString
            .split('&')
            .filter(val => !val.includes('search='))
            .join('&')
          setQueryStringFilterSet(newQueryString)
          handleFilterItemClick('', checkboxesToSet, newQueryString)
        }
      } else if (window.location.href.includes('search=')) {
        const searchParam = window.location.href.split('search=')[1]
        addSearchQuery(queryStringFilterSet, searchParam)
      }
    }
  }

  useEffect(() => {
    let searchValue = UrlUtil.getUrlParameter('search').replace(/-/g, ' ')
    //* Initial page load, set search text if it exist to trigger resource filtering
    if (searchValue && searchText != searchValue) {
      setSearchText(searchValue)
      setShowSearch(true)
    }
  }, [])

  useEffect(() => {
    init()
  }, [checkboxFilters])

  useEffect(() => {
    if (filtersAdded && queryString && queryString !== '' && searchText?.length >= 3) {
      filteredResources(
        resourceData,
        listingData,
        setListingData,
        searchText,
        checkboxFilters,
        filtersAdded
      )
    }
  }, [filtersAdded, searchText])

  useEffect(() => {
    updateURL(queryString, defaultSlug)
  }, [queryString])

  const microcopyData = getMicrocopyData(
    listingPageData.microcopy,
    pageContext.microcopySetResources
  )

  const [resourcesToShow, filtersToShow] = filteredResources(
    resourceData,
    listingData,
    setListingData,
    searchText,
    checkboxFilters,
    filtersAdded
  )

  const handleDrawerOpen = () => {
    if (mobileDrawerOpen === false && typeof window !== undefined) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    setMobileDrawerOpen(!mobileDrawerOpen)
  }

  const pageInner = (
    <>
      <SEOTags
        seoMetadata={listingPageData.seoMetadata}
        path={path}
        slug={defaultSlug}
        locale={defaultLocale}
        isPaginated={true}
        pageNum={currentPage}
        isLastPage={currentPage === Math.ceil(resourceData.length / resourcesPerPage)}
        hrefForPage={hrefForPage}
        availableLocales={pageContext.availableLocales}
      />

      <ResourcePageOuter $useCase={listingPageData.useCase} data-cy="ListingPage">
        {dynamicPageContent}

        <ResourceListingContainer $useCase={listingPageData.useCase}>
          <ResourceListingBodySection
            searchText={searchText}
            filtersToShow={filtersToShow}
            resourcesToShow={resourcesToShow || []}
            eventsData={eventsData}
            start={start}
            end={end}
            resourcesPerPage={resourcesPerPage}
            currentPage={currentPage}
            hrefForPage={hrefForPage}
            handlePaginationClick={handlePaginationClick}
            showSearch={showSearch}
            clearSearch={clearSearch}
            centerTabs={pageContext.listingTabs?.pageTabs || []}
            handleDrawerOpen={handleDrawerOpen}
            listingPageData={listingPageData}
            checkboxFilters={checkboxFilters}
            handleFilterItemClick={handleFilterItemClick}
            filterDrawers={filterDrawers}
            mobileDrawerOpen={mobileDrawerOpen}
            defaultLocale={defaultLocale}
            resetFilterState={resetFilterState}
            setSearchText={setSearchText}
            setQueryString={setQueryString}
            microcopyData={microcopyData}
            featuredResources={featuredResources}
            topThreeFilters={pageContext.topThreeFilters || []}
            filtersAdded={filtersAdded}
            setCheckboxFilters={setCheckboxFilters}
            setFiltersAdded={setFiltersAdded}
            setQueryStringFilterSet={setQueryStringFilterSet}
            setShowSearch={setShowSearch}
          />
        </ResourceListingContainer>
      </ResourcePageOuter>
    </>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      pageType="resourceListing"
      slug={defaultSlug}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      availableLocales={pageContext.availableLocales}>
      <div data-cy={`ResourceCenterLandingPage-${listingPageData.useCase}`}>{pageInner}</div>
    </Layout.Template>
  )
}

export default ResourceCenterLandingPage
